// Migrated from transition-web/src/components/SignInLandingEmailForm.tsx
// Updated to use new design system

import React, { useCallback, useRef } from 'react'
import { useTranslation } from '../shared/hooks/useIntractionTranslate'
import { Box, Stack } from '@mui/material'
import { LockClosedIcon } from '@heroicons/react/24/solid'
import { SignInViewProps } from '../../../pure-js/types/SignInTypes'
import { Button, Input } from '../interaxDesignSystemComponents'
import { isValidEmail } from '../shared/helpers/emailValidation.helper'
import { INTERAX_COLORS } from '../constants/colors'
import { TESTIDS } from '../config/testids'

// Custom debounce hook
const useDebounce = (callback: () => void, delay: number) => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  return useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    timeoutRef.current = setTimeout(callback, delay)
  }, [callback, delay])
}

export default function SignInLandingEmailForm(props: SignInViewProps) {
  const { signInState, onPressContinue, isLoading, disableEditEmail, serverError } = props
  const { t } = useTranslation()
  const [email, setEmail] = React.useState(signInState.data.email || '')
  const [error, setError] = React.useState('')
  const [isValidating, setIsValidating] = React.useState(false)

  // Use ref to track current email value to avoid stale closures
  const emailRef = useRef(email)

  // Keep the local input in sync with prefilled email from the parent (e.g. when
  // arriving via an org invite link the parent sets signInState.data.email
  // after mount).
  React.useEffect(() => {
    const prefilled = signInState.data.email || ''
    if (prefilled && prefilled !== emailRef.current) {
      setEmail(prefilled)
      emailRef.current = prefilled
    }
  }, [signInState.data.email])

  // Use centralized email validation
  const validateEmail = isValidEmail

  // Debounced validation function
  const debouncedValidate = useDebounce(() => {
    const currentEmail = emailRef.current

    if (currentEmail && currentEmail.length > 3) {
      setIsValidating(true)
      // Simulate async validation (you can replace this with actual async validation)
      setTimeout(() => {
        const trimmedEmail = currentEmail.trim()
        const isValid = validateEmail(trimmedEmail)

        if (!isValid) {
          setError(t('auth.emailInvalid'))
        } else {
          setError('')
        }
        setIsValidating(false)
      }, 100) // Short delay to show validation state
    } else if (currentEmail.length <= 3 && currentEmail.length > 0) {
      // Clear error for short emails that are still being typed
      setError('')
    }
  }, 500)

  // Real-time validation
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value

    setEmail(value)
    emailRef.current = value // Update ref immediately

    // Clear error when user starts typing
    if (error) {
      setError('')
    }

    // Trigger debounced validation
    debouncedValidate()
  }

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault()

    // Enhanced email validation
    if (!email.trim()) {
      setError(t('auth.emailRequired'))
      return
    }

    const trimmedEmail = email.trim()
    const isValid = validateEmail(trimmedEmail)

    if (!isValid) {
      setError(t('auth.emailInvalid'))
      return
    }

    setError('')
    onPressContinue({
      ...signInState,
      data: { ...signInState.data, email: trimmedEmail }
    })
  }

  // When the email is locked by an invite, render a read-only display card
  // instead of a disabled <Input>. The MUI disabled state dims the text to
  // near-invisible on the dark theme — for an invite acceptance flow the
  // user *must* be able to clearly read their own email to confirm it.
  const renderLockedEmail = () => (
    <Stack spacing={1.25}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 1.75,
          borderRadius: '8px',
          backgroundColor: INTERAX_COLORS.offBlack,
          border: `1px solid ${INTERAX_COLORS.thunderGrey}`,
          color: INTERAX_COLORS.white
        }}
        data-testid="locked-invite-email"
      >
        <LockClosedIcon style={{ width: 18, height: 18, color: INTERAX_COLORS.sun, flexShrink: 0 }} />
        <Box
          component="span"
          sx={{
            fontSize: { xs: '15px', sm: '16px' },
            fontWeight: 500,
            color: INTERAX_COLORS.white,
            wordBreak: 'break-all'
          }}
        >
          {email}
        </Box>
      </Box>
      <Box
        sx={{
          fontSize: '13px',
          color: INTERAX_COLORS.white70,
          lineHeight: 1.5
        }}
      >
        {t('auth.inviteLockedEmailHelper')}
      </Box>
    </Stack>
  )

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {disableEditEmail ? (
        renderLockedEmail()
      ) : (
        <Box>
          <Input
            value={email}
            onChange={handleChange}
            onEnterPress={handleSubmit}
            placeholder={t('auth.emailPlaceholder')}
            type="email"
            disabled={isLoading}
            required
            error={!!error}
            errorMessage={error}
            fullWidth
            size="large"
            className="w-full"
            data-testid={TESTIDS.AUTH_EMAIL_INPUT}
          />
          {error && <Box className="mt-2 text-sm text-red-400">{error}</Box>}
          {isValidating && <Box className="mt-2 text-sm text-gray-400">{t('auth.validatingEmail')}</Box>}
        </Box>
      )}

      {!error && serverError && (
        <Box className="text-sm text-red-400" data-testid={TESTIDS.AUTH_NOT_INVITED_ERROR} role="alert">
          {serverError}
        </Box>
      )}

      <Button
        variant="primary"
        fullWidth
        onClick={handleSubmit}
        disabled={isLoading || !email.trim() || isValidating}
        className="h-12"
        data-testid={TESTIDS.AUTH_EMAIL_CONTINUE_BTN}
      >
        {isLoading ? t('auth.sending') : disableEditEmail ? t('auth.continueLabel') : t('auth.continueWithEmail')}
      </Button>
    </form>
  )
}
